/**
 * Role dropdown options for users admin (bootstrap + fallback).
 */

import { getBootstrapData } from '@tpct/admin-ui';
import type { UnifiedSearchableSelectOption } from '@tpct/admin-ui';
import type { RoleOption } from '@/users/src/types/user';
import { __ } from '@/hub-shared/utils/i18n';

type UsersBootstrapWithRoles = {
  roleOptions?: RoleOption[];
};

const hasRoleOptions = (value: unknown): value is UsersBootstrapWithRoles =>
  value !== null
  && typeof value === 'object'
  && 'roleOptions' in value
  && Array.isArray((value as UsersBootstrapWithRoles).roleOptions);

export const DEFAULT_NEW_USER_ROLE = 'collaborateur_comptable';

const fallbackRoleOptions = (): RoleOption[] => [
  { value: 'collaborateur_comptable', label: __('Collaborateur comptable'), allowed: true, reason: null, message: null },
  { value: 'expert_comptable', label: __('Expert-comptable'), allowed: true, reason: null, message: null },
];

export const resolveDefaultNewUserRole = (roles: RoleOption[]): string => {
  const preferred = roles.find(
    (role) => role.value === DEFAULT_NEW_USER_ROLE && role.allowed,
  );
  if (preferred !== undefined) {
    return preferred.value;
  }

  return roles.find((role) => role.allowed)?.value ?? DEFAULT_NEW_USER_ROLE;
};

export const readRoleOptions = (bootstrapKey: string): RoleOption[] => {
  const bootstrap: unknown = getBootstrapData(bootstrapKey);
  return hasRoleOptions(bootstrap) && bootstrap.roleOptions !== undefined && bootstrap.roleOptions.length > 0
    ? bootstrap.roleOptions
    : fallbackRoleOptions();
};

export const formatRoleOptionLabel = (role: RoleOption): string =>
  role.allowed || !role.message ? role.label : `${role.label} — ${role.message}`;

export const buildRoleSelectOptions = (bootstrapKey: string): UnifiedSearchableSelectOption[] => {
  const roles = readRoleOptions(bootstrapKey);
  const ordered = [...roles.filter((role) => role.allowed), ...roles.filter((role) => !role.allowed)];
  return ordered.map((role) => ({
    value: role.value,
    label: formatRoleOptionLabel(role),
    disabled: !role.allowed,
    searchText: `${role.label} ${role.message ?? ''}`.trim(),
  }));
};
